import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { MotionConfig, motion, useReducedMotion, type Variants } from "framer-motion";

export interface HeroCTA {
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: "primary" | "secondary";
}

export interface HeroImage {
  src: string;
  alt: string;
}

interface PageHeroProps {
  eyebrow?: string;
  headline: ReactNode;
  supporting?: ReactNode;
  /** When provided, replaces `supporting` with a quote synced to the image rotation. */
  rotatingQuotes?: string[];
  ctas?: HeroCTA[];
  trustBar?: string;
  align?: "left" | "center";
  images?: HeroImage[];
  /** Container width; "6xl" matches the Section component used on inner pages. */
  maxWidth?: "5xl" | "6xl";
  /** Optional photo backdrop behind a purple overlay. */
  backgroundImage?: string;
  /** CSS object-position for the backdrop photo. */
  backgroundPosition?: string;
  /** Light text treatment for use over the purple backdrop. */
  dark?: boolean;
  /** Adds room for an overlaying transparent header (homepage). */
  headerInset?: boolean;
  /** Floating keyword labels around the portrait. */
  showKeywordLabels?: boolean;
  /** Trims ~12px off the top inset (used on /home-2). */
  tightTop?: boolean;
  /** Scale of the central image group (portrait + shapes). Default 1.155. */
  imageGroupScale?: number;
  /** Fill color of the dominant background triangle. Default "#fff". */
  shapeColor?: string;
  /** Fill opacity of the dominant background triangle. Default "0.143". */
  shapeOpacity?: string;
  /** Fill opacity of the mid-sized background triangle. Default "0.3". */
  midShapeOpacity?: string;
  /** Stroke color of the pentagon accent outline. Default "#fff". */
  pentagonColor?: string;
  /** Name/title labels shown right of the portrait, synced to the image rotation. */
  personLabels?: { name: string; title: string }[];
  /** Heading tag for the headline. Use "h2" when the hero is not the page's main heading. */
  headingLevel?: "h1" | "h2";
}

const ROTATE_MS = 14000;
/** ch of box width per quote character; tuned so each quote balances onto two lines at 768px+. */
const QUOTE_CH_FACTOR = 0.5;

/** Ever-incrementing tick; consumers take their own modulo so lists of
 *  different lengths cycle independently off one shared timer. */
function useRotatingTick(active: boolean) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const id = window.setInterval(() => setTick((t) => t + 1), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [active]);

  return tick;
}

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.135, delayChildren: 0.1 } },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE_OUT } },
};

const labelsGroup: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

// reduced motion: no entrance animation, elements render in final state
const instant: Variants = {
  hidden: { opacity: 1, y: 0, scale: 1 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0 } },
};

const fadeScale: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: EASE_OUT },
  },
};

function KeywordLabel({
  word,
  hollowIndex,
  animate,
  align = "center",
}: {
  word: string;
  hollowIndex: number;
  animate: boolean;
  align?: "left" | "right" | "center";
}) {
  const textAlign =
    align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center";
  const justify =
    align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
  return (
    <motion.div
      variants={animate ? fadeUp : instant}
      className={`lel-keyword-label select-none ${textAlign}`}
    >
      <p
        className={`text-lg font-normal tracking-wide text-foreground/40 lg:text-xl ${align === "right" ? "-mr-[0.5px]" : ""}`}
      >
        {word}
      </p>

      <div className={`mt-2 flex items-center ${justify} gap-1.5`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <span
            key={i}
            className={`block h-1.5 w-1.5 rounded-full ${
              i === hollowIndex ? "border border-white/80" : "bg-white/80"
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
}

export function PageHero({
  eyebrow,
  headline,
  supporting,
  rotatingQuotes,
  ctas,
  trustBar,
  align = "left",
  images,
  maxWidth = "5xl",
  backgroundImage,
  backgroundPosition,
  dark = false,
  headerInset = false,
  showKeywordLabels = true,
  tightTop = false,
  imageGroupScale = 1.155,
  shapeColor = "#fff",
  shapeOpacity = "0.143",
  midShapeOpacity = "0.3",
  pentagonColor = "#fff",
  personLabels,
  headingLevel = "h1",
}: PageHeroProps) {
  const isCenter = align === "center";
  const heroImages = images ?? [];
  const quotes = rotatingQuotes ?? [];
  // one shared timer drives both crossfades; with portraits, quote i is fixed to person i
  const tick = useRotatingTick(heroImages.length > 1 || quotes.length > 1);
  const activeIndex = heroImages.length ? tick % heroImages.length : 0;
  const activeQuoteIndex = heroImages.length
    ? activeIndex
    : quotes.length
      ? tick % quotes.length
      : 0;
  const reduceMotion = useReducedMotion();
  // prefers-reduced-motion: no entrance animation at all — render final state.
  const entrance = {
    variants: containerVariants,
    initial: "hidden" as const,
    whileInView: "visible" as const,
    // pre-trigger: expand the detection box 25% above/below the viewport so the
    // sequence is already underway before the hero is actually on screen.
    viewport: { once: false, amount: 0.15, margin: "25% 0px 25% 0px" },
  };

  return (
    <MotionConfig reducedMotion="never">
      <section
        className="relative overflow-hidden"
        style={
          backgroundImage
            ? undefined
            : dark
              ? { background: "#693D94" }
              : {
                  background:
                    "linear-gradient(160deg, #EFECF4 0%, #DFD8E9 42%, #EAE5F1 68%, var(--background) 100%)",
                }
        }
      >
        {backgroundImage && (
          <>
            <img
              src={backgroundImage}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: backgroundPosition ?? "center top" }}
            />
            <div className="absolute inset-0 bg-[#693D94] opacity-[0.86]" aria-hidden="true" />
          </>
        )}

        <motion.div
          {...entrance}
          className={`relative mx-auto ${maxWidth === "6xl" ? "max-w-6xl" : "max-w-5xl"} px-6 ${backgroundImage ? "pt-12 pb-14 md:pt-16 md:pb-20" : headerInset ? (tightTop ? "pt-[calc(1.296rem+68px)] pb-12 md:pt-[calc(1.728rem+68px)] md:pb-16" : "pt-[calc(1.296rem+80px)] pb-12 md:pt-[calc(1.728rem+80px)] md:pb-16") : "pt-[1.296rem] pb-12 md:pt-[1.728rem] md:pb-16"} ${isCenter ? "text-center" : ""}`}
        >
          {eyebrow && (
            <p
              className={`mb-5 inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider ${dark ? "border border-white/30 bg-white/10 text-white" : "border border-primary/20 bg-background/70 text-primary"}`}
            >
              {eyebrow}
            </p>
          )}
          {(() => {
            const Heading = headingLevel === "h2" ? motion.h2 : motion.h1;
            return (
              <Heading
                variants={reduceMotion ? instant : fadeUp}
                className={`relative z-20 font-display text-[1.575rem] font-bold leading-[1.05] tracking-tight md:text-[2.625rem] lg:text-[2.975rem] ${dark ? "text-white" : "text-ink"}`}
              >
                {headline}
              </Heading>
            );
          })()}

          {heroImages.length > 0 && (
            <div className={`relative mt-14 mb-8 ${isCenter ? "mx-auto" : ""} w-full max-w-3xl`}>
              {showKeywordLabels && (
                <motion.div
                  variants={reduceMotion ? instant : labelsGroup}
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[5] hidden md:block"
                >
                  <div className="absolute left-0 top-[12%] -translate-x-[35px]">
                    <KeywordLabel
                      align="left"
                      word="Engagement"
                      hollowIndex={1}
                      animate={!reduceMotion}
                    />
                  </div>
                  <div className="absolute left-0 top-[46%] -translate-x-[35px]">
                    <KeywordLabel
                      align="left"
                      word="Live Sessions"
                      hollowIndex={4}
                      animate={!reduceMotion}
                    />
                  </div>
                  <div className="absolute left-0 top-[78%] -translate-x-[35px]">
                    <KeywordLabel
                      align="left"
                      word="Productivity"
                      hollowIndex={2}
                      animate={!reduceMotion}
                    />
                  </div>
                  <div className="absolute right-0 top-[12%] translate-x-[35px]">
                    <KeywordLabel
                      align="right"
                      word="Customizable"
                      hollowIndex={5}
                      animate={!reduceMotion}
                    />
                  </div>
                  <div className="absolute right-0 top-[46%] translate-x-[35px]">
                    <KeywordLabel
                      align="right"
                      word="Transformation"
                      hollowIndex={0}
                      animate={!reduceMotion}
                    />
                  </div>
                  <div className="absolute right-0 top-[78%] translate-x-[35px]">
                    <KeywordLabel
                      align="right"
                      word="Practical"
                      hollowIndex={3}
                      animate={!reduceMotion}
                    />
                  </div>
                </motion.div>
              )}
              {/* static (not part of the entrance sequence): always fully visible.
                crossfade + shape hover/spin live inside, untouched */}
              <div className="relative">
                <div
                  className="relative mx-auto w-full max-w-sm"
                  style={{ transform: `scale(${imageGroupScale})` }}
                >
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[165%] w-[165%] -translate-x-1/2 -translate-y-1/2"
                    viewBox="0 0 400 400"
                    fill="none"
                  >
                    {/* rounded "squircle" triangles echoing the logo mark */}
                    {/* dominant background shape */}
                    <g transform="rotate(-16 200 200) translate(165 172) scale(1.62) translate(-200 -200)">
                      <path
                        className="lel-hero-shape"
                        d="M200 46c14 0 26 8 33 20l104 180c7 12 7 26 0 38s-19 19-33 19H96c-14 0-26-7-33-19s-7-26 0-38L167 66c7-12 19-20 33-20z"
                        fill={shapeColor}
                        fillOpacity={shapeOpacity}
                      />
                    </g>
                    {/* mid-sized, offset */}
                    <g transform="rotate(8 200 200) translate(190 178) scale(0.972) translate(-200 -200)">
                      <path
                        className="lel-hero-shape"
                        d="M200 46c14 0 26 8 33 20l104 180c7 12 7 26 0 38s-19 19-33 19H96c-14 0-26-7-33-19s-7-26 0-38L167 66c7-12 19-20 33-20z"
                        fill={shapeColor}
                        fillOpacity={midShapeOpacity}
                      />
                    </g>
                    {/* thin outline, small pentagon accent layer */}
                    <g transform="rotate(4 200 200) translate(175 196) scale(0.847) translate(-200 -200)">
                      <g className="lel-pentagon-spin">
                        <path
                          className="lel-hero-shape"
                          d="M219.4 60.1 L327.1 138.3 Q346.5 152.4 339.1 175.2 L297.9 301.8 Q290.5 324.6 266.5 324.6 L133.5 324.6 Q109.5 324.6 102.1 301.8 L60.9 175.2 Q53.5 152.4 72.9 138.3 L180.6 60.1 Q200 46 219.4 60.1 Z"
                          strokeLinejoin="round"
                          stroke={pentagonColor}
                          strokeOpacity="0.55"
                          strokeWidth="3"
                        />
                      </g>
                    </g>
                  </svg>
                  <div className="pointer-events-none relative z-10 mx-auto grid w-full">
                    {heroImages.map((img, i) => (
                      <img
                        key={img.src}
                        src={img.src}
                        alt={i === activeIndex ? img.alt : ""}
                        aria-hidden={i === activeIndex ? undefined : true}
                        loading="eager"
                        className={`lel-hero-portrait col-start-1 row-start-1 mx-auto h-auto w-full max-h-[320px] rounded-2xl object-contain ${
                          i === activeIndex ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                {personLabels && personLabels.length > 0 && (
                  <div
                    data-testid="hero-person-label"
                    className="pointer-events-none absolute z-[6] hidden -translate-y-1/2 grid text-left md:grid"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      left: "calc(50% + 140px)",
                      top: "calc(50% + 100px)",
                    }}
                  >
                    {personLabels.map((person, i) => (
                      <div
                        key={person.name}
                        aria-hidden={i === activeIndex ? undefined : true}
                        className={`lel-hero-portrait col-start-1 row-start-1 ${
                          i === activeIndex ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        <p
                          className={`whitespace-nowrap text-base font-bold leading-tight md:text-lg ${dark ? "text-white" : "text-ink"}`}
                        >
                          {person.name}
                        </p>
                        <p
                          className={`mt-1 whitespace-nowrap text-sm font-normal leading-tight ${dark ? "text-white/80" : "text-foreground/70"}`}
                        >
                          {person.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {quotes.length > 0 ? (
            <motion.div
              variants={reduceMotion ? instant : fadeUp}
              style={{ fontFamily: "'Poppins', sans-serif" }}
              className={`relative left-1/2 -translate-x-1/2 mt-[44px] grid w-[1180px] max-w-[92vw] text-[1.3125rem] leading-relaxed md:text-[1.4375rem] ${dark ? "text-white/90" : "text-foreground"}`}
            >
              {quotes.map((quote, i) => (
                <p
                  key={quote}
                  aria-hidden={i === activeQuoteIndex ? undefined : true}
                  data-hero-quote={i === activeQuoteIndex ? "active" : "inactive"}
                  // 768px+: box width scales with this quote's own length so balance yields two even lines
                  style={{ ["--quote-w" as string]: `${Math.ceil((quote.length + 2) * QUOTE_CH_FACTOR)}ch` }}
                  className={`col-start-1 row-start-1 text-balance md:mx-auto md:w-full md:max-w-[var(--quote-w)] ${reduceMotion ? "" : "transition-opacity duration-700 ease-out"} ${i === activeQuoteIndex ? "opacity-100" : "opacity-0"}`}
                >
                  {"\u201C"}
                  {quote}
                  {"\u201D"}
                </p>
              ))}
            </motion.div>
          ) : supporting ? (
            <motion.p
              variants={reduceMotion ? instant : fadeUp}
              style={{ fontFamily: "'Poppins', sans-serif" }}
              className={`mt-6 max-w-2xl text-[1rem] leading-relaxed md:text-[1.125rem] ${dark ? "text-white/90" : "text-foreground"} ${isCenter ? "mx-auto" : ""}`}
            >
              {supporting}
            </motion.p>
          ) : null}
          {ctas && ctas.length > 0 && (
            <motion.div
              variants={reduceMotion ? instant : fadeUp}
              className={`${quotes.length > 0 ? "mt-4" : "mt-8"} flex flex-wrap gap-3 ${isCenter ? "justify-center" : ""}`}
            >
              {ctas.map((cta, i) => {
                const isPrimary =
                  (cta.variant ?? (i === 0 ? "primary" : "secondary")) === "primary";
                const cls = isPrimary
                  ? dark
                    ? "inline-flex items-center rounded-[6px] bg-white px-6 py-3 text-sm font-semibold text-[#693D94] transition-transform hover:-translate-y-0.5"
                    : "inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  : dark
                    ? "inline-flex items-center rounded-[6px] border border-white/60 bg-transparent px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-white/10"
                    : "inline-flex items-center rounded-[6px] border border-neutral-700 bg-transparent px-6 py-3 text-sm font-semibold text-neutral-700 transition-transform hover:-translate-y-0.5 hover:bg-neutral-700/5";
                if (cta.onClick || !cta.to) {
                  return (
                    <button key={cta.label} type="button" onClick={cta.onClick} className={cls}>
                      {cta.label}
                    </button>
                  );
                }
                return (
                  <Link key={cta.to + cta.label} to={cta.to} className={cls}>
                    {cta.label}
                  </Link>
                );
              })}
            </motion.div>
          )}
          {trustBar && (
            <p
              className={`mt-8 text-xs normal-case tracking-normal md:uppercase md:tracking-[0.18em] ${dark ? "text-white/70" : "text-muted-foreground"}`}
            >
              {trustBar}
            </p>
          )}
        </motion.div>
      </section>
    </MotionConfig>
  );
}
